import { apiClient } from './client';

export interface KnowledgeBaseFolder {
  id: string;
  name: string;
  parentId: string | null;
  path: string;
  description: string | null;
  visaTypes: string[];
  createdAt: string;
  updatedAt: string;
  children?: KnowledgeBaseFolder[];
  documentCount?: number;
}

export interface KnowledgeBaseDocument {
  id: string;
  folderId: string | null;
  title: string;
  content: string;
  contentType: string;
  tags: string[];
  visaTypes: string[];
  metadata: Record<string, unknown>;
  blobUrl: string | null;
  fileType: string | null;
  fileSize: number | null;
  processingStatus: string;
  isEmbedded: boolean;
  embeddingError: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  folder?: {
    id: string;
    name: string;
    path: string;
  };
}

export interface CreateFolderRequest {
  name: string;
  parentId?: string;
  description?: string;
  visaTypes?: string[];
}

export interface UpdateFolderRequest {
  name?: string;
  description?: string;
  visaTypes?: string[];
}

export interface CreateDocumentRequest {
  title: string;
  content: string;
  contentType: string;
  folderId?: string;
  tags?: string[];
  visaTypes?: string[];
  metadata?: Record<string, unknown>;
}

export interface UpdateDocumentRequest {
  title?: string;
  content?: string;
  contentType?: string;
  folderId?: string;
  tags?: string[];
  visaTypes?: string[];
  metadata?: Record<string, unknown>;
}

export interface SearchDocumentsRequest {
  query?: string;
  contentType?: string;
  visaTypes?: string[];
  tags?: string[];
  folderId?: string;
  limit?: number;
  offset?: number;
}

export interface SemanticSearchRequest {
  query: string;
  visaTypes?: string[];
  contentType?: string;
  limit?: number;
}

export interface SemanticSearchResult {
  document: KnowledgeBaseDocument;
  similarity: number;
  relevantChunk?: string;
}

export interface AutoLabelRequest {
  content: string;
  title?: string;
  contentType?: string;
}

export interface AutoLabelResponse {
  suggestedTags: Array<{ tag: string; confidence: number }>;
  suggestedVisaTypes: Array<{ visaType: string; confidence: number }>;
  suggestedContentType: { type: string; confidence: number };
}

// Folder APIs
export const knowledgeBaseFolderApi = {
  create: async (data: CreateFolderRequest): Promise<KnowledgeBaseFolder> => {
    return apiClient.post(`/knowledge-base/folders`, data);
  },

  list: async (parentId?: string): Promise<KnowledgeBaseFolder[]> => {
    const params = parentId ? `?parentId=${parentId}` : '';
    return apiClient.get(`/knowledge-base/folders${params}`);
  },

  getTree: async (): Promise<KnowledgeBaseFolder[]> => {
    return apiClient.get(`/knowledge-base/folders/tree`);
  },

  get: async (id: string, includeChildren = false): Promise<KnowledgeBaseFolder> => {
    const params = includeChildren ? '?includeChildren=true' : '';
    return apiClient.get(`/knowledge-base/folders/${id}${params}`);
  },

  update: async (id: string, data: UpdateFolderRequest): Promise<KnowledgeBaseFolder> => {
    return apiClient.patch(`/knowledge-base/folders/${id}`, data);
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/knowledge-base/folders/${id}`);
  },
};

// Document APIs
export const knowledgeBaseDocumentApi = {
  create: async (data: CreateDocumentRequest): Promise<KnowledgeBaseDocument> => {
    return apiClient.post(`/knowledge-base/documents`, data);
  },

  search: async (
    params: SearchDocumentsRequest
  ): Promise<{ documents: KnowledgeBaseDocument[]; total: number }> => {
    const queryParams = new URLSearchParams();
    if (params.query) queryParams.append('query', params.query);
    if (params.contentType) queryParams.append('contentType', params.contentType);
    if (params.folderId) queryParams.append('folderId', params.folderId);
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.offset) queryParams.append('offset', params.offset.toString());
    if (params.visaTypes) params.visaTypes.forEach(vt => queryParams.append('visaTypes', vt));
    if (params.tags) params.tags.forEach(tag => queryParams.append('tags', tag));

    const queryString = queryParams.toString();
    return apiClient.get(`/knowledge-base/documents${queryString ? `?${queryString}` : ''}`);
  },

  get: async (id: string): Promise<KnowledgeBaseDocument> => {
    return apiClient.get(`/knowledge-base/documents/${id}`);
  },

  update: async (id: string, data: UpdateDocumentRequest): Promise<KnowledgeBaseDocument> => {
    return apiClient.patch(`/knowledge-base/documents/${id}`, data);
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/knowledge-base/documents/${id}`);
  },

  uploadFile: async (
    file: File,
    metadata: {
      title?: string;
      contentType: string;
      folderId?: string;
      tags?: string[];
      visaTypes?: string[];
    }
  ): Promise<{ documentId: string }> => {
    console.log('[knowledgeBaseDocumentApi] Preparing file upload:', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      metadata,
    });

    const formData = new FormData();
    formData.append('file', file);
    if (metadata.title) formData.append('title', metadata.title);
    formData.append('contentType', metadata.contentType);
    if (metadata.folderId) formData.append('folderId', metadata.folderId);
    if (metadata.tags && metadata.tags.length > 0) formData.append('tags', metadata.tags.join(','));
    if (metadata.visaTypes && metadata.visaTypes.length > 0) formData.append('visaTypes', metadata.visaTypes.join(','));

    console.log('[knowledgeBaseDocumentApi] Sending FormData to /knowledge-base/documents/upload');

    try {
      const result = await apiClient.post(`/knowledge-base/documents/upload`, formData);
      console.log('[knowledgeBaseDocumentApi] Upload successful:', result);
      return result as { documentId: string };
    } catch (error) {
      console.error('[knowledgeBaseDocumentApi] Upload failed:', error);
      throw error;
    }
  },

  embed: async (id: string): Promise<void> => {
    await apiClient.post(`/knowledge-base/documents/${id}/embed`, {});
  },
};

// Search APIs
export const knowledgeBaseSearchApi = {
  semanticSearch: async (data: SemanticSearchRequest): Promise<SemanticSearchResult[]> => {
    return apiClient.post(`/knowledge-base/search`, data);
  },

  getRelevantContext: async (
    query: string,
    visaTypes?: string[],
    limit = 5
  ): Promise<Array<{ title: string; content: string; similarity: number }>> => {
    return apiClient.post(`/knowledge-base/suggest`, {
      query,
      visaTypes,
      limit,
    });
  },
};

// AI APIs
export const knowledgeBaseAIApi = {
  autoLabel: async (data: AutoLabelRequest): Promise<AutoLabelResponse> => {
    return apiClient.post(`/knowledge-base/label`, data);
  },
};
